import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../../assets/logo.svg';
import './Header.scss';

interface HeaderProps {
  cartCount?: number;
  onCartClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ cartCount = 0, onCartClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  // HomePage and Archive pages have transparent header, all other pages have white background
  const isHomePage = location.pathname === '/';
  const isArchivePage = location.pathname === '/archive' || location.pathname.startsWith('/archive/');

  return (
    <header className={`header ${!isHomePage && !isArchivePage ? 'header--white-bg' : ''} ${isMobileMenuOpen ? 'header--mobile-menu-open' : ''}`}>
      <div className="header__container">
        {/* Logo - Left on mobile, center on desktop */}
        <div className="header__brand">
          <Link to="/" className="header__brand-link" onClick={closeMobileMenu}>
            <img src={logo} alt="Shtrom" className="header__logo" />
          </Link>
        </div>

        {/* Main Navigation - Hidden on mobile, shown in menu */}
        <nav className={`header__nav ${isMobileMenuOpen ? 'header__nav--open' : ''}`}>
          <Link 
            to="/about" 
            className={`header__nav-link ${isActive('/about') ? 'header__nav-link--active' : ''}`}
            onClick={closeMobileMenu}
          >
            Shtrom
          </Link>
          <Link 
            to="/archive" 
            className={`header__nav-link ${isActive('/archive') ? 'header__nav-link--active' : ''}`}
            onClick={closeMobileMenu}
          >
            Archive
          </Link>
          {/* <Link 
            to="/lookbook" 
            className={`header__nav-link ${isActive('/lookbook') ? 'header__nav-link--active' : ''}`}
            onClick={closeMobileMenu}
          >
            Lookbook
          </Link> */}
        </nav>

        {/* Right Side Actions - Hidden on mobile, shown in menu */}
        <div className={`header__actions ${isMobileMenuOpen ? 'header__actions--open' : ''}`}>
          <button onClick={() => { onCartClick?.(); closeMobileMenu(); }} className="header__cart">
            Cart ({cartCount})
          </button>
        </div>

        {/* Burger Menu Button - Only on mobile */}
        <button 
          className="header__burger"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="header__burger-line"></span>
          <span className="header__burger-line"></span>
          <span className="header__burger-line"></span>
        </button>
      </div>

      {/* Full Screen Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'mobile-menu--open' : ''}`}>
        <div className="mobile-menu__inner">
          {/* Primary Navigation */}
          <nav className="mobile-menu__nav">
            <Link 
              to="/about" 
              className="mobile-menu__link mobile-menu__link--primary"
              onClick={closeMobileMenu}
            >
              Shtrom
            </Link>
            <Link 
              to="/archive" 
              className="mobile-menu__link mobile-menu__link--primary"
              onClick={closeMobileMenu}
            >
              Archive
            </Link>
            {/* <Link 
              to="/lookbook" 
              className="mobile-menu__link mobile-menu__link--primary"
              onClick={closeMobileMenu}
            >
              Lookbook
            </Link> */}
          </nav>

          {/* Actions */}
          <div className="mobile-menu__actions">
            <button 
              className="mobile-menu__action-link"
              onClick={() => { onCartClick?.(); closeMobileMenu(); }}
            >
              Cart ({cartCount})
            </button>
          </div>

          {/* Footer Links */}
          <div className="mobile-menu__footer">
            <div className="mobile-menu__footer-links">
              <Link to="/terms" className="mobile-menu__footer-link" onClick={closeMobileMenu}>
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="mobile-menu__footer-link" onClick={closeMobileMenu}>
                Privacy Policy
              </Link>
              <Link to="/contact" className="mobile-menu__footer-link" onClick={closeMobileMenu}>
                Contact Us
              </Link>
              <Link to="/about" className="mobile-menu__footer-link" onClick={closeMobileMenu}>
                About Us
              </Link>
            </div>

            <div className="mobile-menu__copyright">
              <span>Shtrom © {new Date().getFullYear()}</span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
